import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, CalendarPlus, CalendarClock, Wallet } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { SetHireDateDialog } from '@/features/members/public';
import { OpenCycleDialog } from './OpenCycleDialog';
import { MemberAdjustmentDialog } from './MemberAdjustmentDialog';

interface MemberCycleActionsProps {
  userId: string;
  displayName?: string;
}

/**
 * Single "···" menu for the three member-entitlement actions that had no
 * shared home before: setting a hire date, opening an entitlement cycle
 * (`TimeOff.MemberHasNoHireDate` blocks this without one), and adjusting a
 * balance. Owner/Admin only.
 */
export function MemberCycleActions({ userId, displayName }: MemberCycleActionsProps) {
  const { t } = useTranslation('timeOff');
  const [hireDateOpen, setHireDateOpen] = useState(false);
  const [openCycleOpen, setOpenCycleOpen] = useState(false);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t('team.members.actionsMenu')}
                data-testid={`time-off-member-actions-${userId}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('team.members.actionsMenu')}</TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => setHireDateOpen(true)}
            data-testid="time-off-member-hire-date-trigger"
          >
            <CalendarPlus className="h-4 w-4" />
            {t('team.members.setHireDate')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setOpenCycleOpen(true)}
            data-testid="time-off-member-open-cycle-btn"
          >
            <CalendarClock className="h-4 w-4" />
            {t('team.members.openCycle')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setAdjustmentOpen(true)}
            data-testid="time-off-member-adjustment-trigger"
          >
            <Wallet className="h-4 w-4" />
            {t('team.members.adjustment')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {hireDateOpen && (
        <SetHireDateDialog
          member={{ userId, displayName }}
          open={hireDateOpen}
          onOpenChange={setHireDateOpen}
        />
      )}

      <OpenCycleDialog
        userId={userId}
        displayName={displayName}
        open={openCycleOpen}
        onOpenChange={setOpenCycleOpen}
      />

      <MemberAdjustmentDialog
        userId={userId}
        displayName={displayName}
        open={adjustmentOpen}
        onOpenChange={setAdjustmentOpen}
      />
    </>
  );
}
